/**
 * Ring permissions - ADR-60 cut 4 (brief section 65).
 *
 * Every ring command has a NAMED permission, and every role has a NAMED grant
 * set. The reducer stays the authority (its role checks are what actually
 * decide), and this table is checked to agree with it by test; the point of
 * naming is that the socket layer can refuse a command before the engine's
 * single writer spends a turn on it, an audit line can say "ring.hammer",
 * and a future per-operator grant (a spotter who may also accept internet
 * bids) is a table edit rather than a reducer change.
 *
 * "Operator role alone should not imply every permission unless explicitly
 * designed that way" - it is designed that way here, explicitly: `clerk`
 * holds every ring permission. That is the current truth of the reducer and
 * this table says so rather than pretending otherwise.
 */

import { INTENT_CONTROL_KEYS } from './types.js';
import type { ActorRole, Command, IntentControls, MaxBidViewerRole, MaxBidViewScope, RingCapabilities, RingStaffRoster } from './types.js';

export type RingPermission =
  | 'ring.set_current_lot'
  | 'ring.open'
  | 'ring.set_ask'
  | 'ring.fair_warning'
  | 'ring.hammer'
  | 'ring.sell'
  | 'ring.no_sale'
  | 'ring.pass'
  | 'ring.reopen'
  | 'ring.correct_floor_bid'
  | 'ring.record_floor_bid'
  | 'ring.pause'
  | 'ring.resume'
  | 'ring.heartbeat'
  | 'ring.finalize'
  | 'ring.set_bidder_approval'
  | 'ring.set_ring_staff'
  | 'ring.tick'
  | 'ring.post_announcement'
  | 'ring.invalidate_max_bids'
  | 'ring.continue_proxy_resolution'
  | 'ring.open_timed_lot'
  | 'ring.reopen_timed_lot'
  | 'ring.accept_online_bid'
  | 'ring.decline_online_bid'
  | 'ring.set_confirm_mode'
  | 'ring.void_sale'
  | 'ring.award_equal_bid'
  | 'ring.restore_equal_bid'
  | 'ring.retract_bid'
  | 'ring.reset_bidding'
  | 'ring.change_increment'
  | 'ring.remove_from_ring'
  | 'ring.set_ring_capabilities'
  | 'ring.view_max_bids'
  | 'bidder.place_bid'
  | 'bidder.place_max_bid'
  | 'bidder.cancel_max_bid'
  | 'bidder.claim_session'
  | 'bidder.acknowledge';

/** The permission each command needs. Exhaustive over the command union: a
 *  new command without a row here does not compile. */
export const COMMAND_PERMISSION: { readonly [T in Command['type']]: RingPermission } = {
  SET_CURRENT_LOT: 'ring.set_current_lot',
  OPEN_LOT: 'ring.open',
  SET_ASK: 'ring.set_ask',
  DECLARE_FAIR_WARNING: 'ring.fair_warning',
  // ADR-70 cut 3: calling the close is the fair warning, step by step.
  SET_CLOSE_STEP: 'ring.fair_warning',
  HAMMER: 'ring.hammer',
  CONFIRM_SOLD: 'ring.sell',
  DECLARE_NO_SALE: 'ring.no_sale',
  DECLARE_PASSED: 'ring.pass',
  // ADR-70 cut 3: the disposition is what the operator decides; the advance
  // half is a SET_CURRENT_LOT the reducer judges as the clerk's like any other.
  SELL_AND_ADVANCE: 'ring.sell',
  PASS_AND_ADVANCE: 'ring.pass',
  REOPEN_LOT: 'ring.reopen',
  CORRECT_FLOOR_BID: 'ring.correct_floor_bid',
  PLACE_FLOOR_BID: 'ring.record_floor_bid',
  PAUSE_ONLINE: 'ring.pause',
  RESUME_ONLINE: 'ring.resume',
  HEARTBEAT: 'ring.heartbeat',
  FINALIZE_SALE: 'ring.finalize',
  SET_BIDDER_APPROVAL: 'ring.set_bidder_approval',
  // ADR-70 cut 5f: the control plane's alone (CONTROL_PLANE_PERMISSIONS).
  SET_RING_STAFF: 'ring.set_ring_staff',
  // ADR-70 cut 6e: the Super Admin's capability switch reaches the ring as a
  // control-plane message, never from a socket (CONTROL_PLANE_PERMISSIONS).
  SET_RING_CAPABILITIES: 'ring.set_ring_capabilities',
  // ADR-70 cut 6e: looking at the maxes on a lot is the clerk's console act,
  // and only while the tenant's capability is on.
  VIEW_MAX_BIDS: 'ring.view_max_bids',
  TICK: 'ring.tick',
  POST_ANNOUNCEMENT: 'ring.post_announcement',
  INVALIDATE_LOT_MAX_BIDS: 'ring.invalidate_max_bids',
  CONTINUE_PROXY_RESOLUTION: 'ring.continue_proxy_resolution',
  OPEN_TIMED_LOT: 'ring.open_timed_lot',
  // ADR-70 cut 6i: the office's timed recovery reaches the ring through the
  // control plane, never from a socket (CONTROL_PLANE_PERMISSIONS).
  REOPEN_TIMED_LOT: 'ring.reopen_timed_lot',
  ACCEPT_ONLINE_BID: 'ring.accept_online_bid',
  DECLINE_ONLINE_BID: 'ring.decline_online_bid',
  SET_CONFIRM_MODE: 'ring.set_confirm_mode',
  // ADR-65: reversing a sale is the clerk's, and only the clerk's, while the
  // ring is authoritative. A spotter never voids; the office door is cut 2.
  VOID_SALE: 'ring.void_sale',
  // ADR-70 cut 2: who wins an equal bid is the auctioneer's call, so the
  // clerk's console makes it; a spotter enters bids, never overrides one.
  AWARD_EQUAL_BID_ONSITE: 'ring.award_equal_bid',
  RESTORE_EQUAL_BID_ONLINE: 'ring.restore_equal_bid',
  // ADR-70 cut 6c: taking back any bid, and resetting the board, are the
  // clerk's; a spotter enters bids and never removes one.
  RETRACT_LAST_BID: 'ring.retract_bid',
  RESET_BIDDING: 'ring.reset_bidding',
  // ADR-70 cut 6d: the lot's increment and taking it out of the ring are the
  // clerk's console acts.
  SET_INCREMENT: 'ring.change_increment',
  REMOVE_FROM_RING: 'ring.remove_from_ring',
  PLACE_BID: 'bidder.place_bid',
  PLACE_MAX_BID: 'bidder.place_max_bid',
  CANCEL_MAX_BID: 'bidder.cancel_max_bid',
  CLAIM_BIDDING_SESSION: 'bidder.claim_session',
  ACKNOWLEDGE_ANNOUNCEMENTS: 'bidder.acknowledge',
};

const RING_ALL: readonly RingPermission[] = [
  'ring.set_current_lot', 'ring.open', 'ring.set_ask', 'ring.fair_warning', 'ring.hammer', 'ring.sell', 'ring.no_sale', 'ring.pass',
  'ring.reopen', 'ring.correct_floor_bid', 'ring.record_floor_bid', 'ring.pause', 'ring.resume', 'ring.heartbeat', 'ring.finalize',
  'ring.set_bidder_approval', 'ring.tick', 'ring.post_announcement', 'ring.invalidate_max_bids', 'ring.continue_proxy_resolution',
  'ring.open_timed_lot', 'ring.accept_online_bid', 'ring.decline_online_bid', 'ring.set_confirm_mode', 'ring.void_sale',
  'ring.award_equal_bid', 'ring.restore_equal_bid', 'ring.retract_bid', 'ring.reset_bidding',
  'ring.change_increment', 'ring.remove_from_ring', 'ring.view_max_bids',
];

/** What each role may do. Mirrors the reducer's role checks exactly. */
export const ROLE_GRANTS: { readonly [R in ActorRole]: ReadonlySet<RingPermission> } = {
  clerk: new Set(RING_ALL),
  // A spotter enters floor bids at the ask, keeps the floor alive, and (cut 2)
  // may answer an internet bid. Never moves the ask, never disposes a lot.
  spotter: new Set<RingPermission>(['ring.record_floor_bid', 'ring.heartbeat', 'ring.accept_online_bid', 'ring.decline_online_bid']),
  bidder: new Set<RingPermission>(['bidder.place_bid', 'bidder.place_max_bid', 'bidder.cancel_max_bid', 'bidder.claim_session', 'bidder.acknowledge']),
  observer: new Set<RingPermission>(),
};

export function permissionFor(type: Command['type']): RingPermission {
  return COMMAND_PERMISSION[type];
}

/** Whether `role` holds the permission `type` needs. The reducer decides;
 *  this is the same answer, available before the command reaches it. */
export function isPermitted(role: ActorRole, type: Command['type']): boolean {
  return ROLE_GRANTS[role].has(COMMAND_PERMISSION[type]);
}

/** The actor id the ring applies control-plane messages as (bidder
 *  approvals, the ring staff roster). A socket's actor id is what its token
 *  proved, and no socket role holds a control-plane permission, so a socket
 *  can never send one of these whatever id it carries. */
export const CONTROL_PLANE_ACTOR_ID = 'control-plane';

/** ADR-70 cut 5f: permissions no socket role is granted. The socket layer
 *  refuses them by name (isPermitted is false for every role); the reducer
 *  accepts them only from CONTROL_PLANE_ACTOR_ID. A clerk who could send the
 *  roster could move themself into any ring. */
export const CONTROL_PLANE_PERMISSIONS: ReadonlySet<RingPermission> = new Set<RingPermission>(['ring.set_ring_staff', 'ring.set_ring_capabilities', 'ring.reopen_timed_lot']);

/**
 * ADR-70 cut 6e (hitlist 9.2, Q13 on its proposal): what each staff role sees
 * of a max bid once the tenant's capability is on. Admin sees all four;
 * a clerk sees that a max exists and whose it is, never the amount or what is
 * left of it. A role not listed sees nothing. Every view is recorded
 * (MAX_BID_VIEWED), whatever it showed.
 */
export const MAX_BID_VIEW_SCOPES: { readonly [R in MaxBidViewerRole]: readonly MaxBidViewScope[] } = {
  admin: ['view_proxy_exists', 'view_proxy_bidder', 'view_proxy_amount', 'view_proxy_remaining_capacity'],
  clerk: ['view_proxy_exists', 'view_proxy_bidder'],
};

/**
 * ADR-70 cut 6f (hitlist 10.3): the operator-intent controls this ring runs
 * under, or null while the tenant's switch is off. A control the control
 * plane did not send is off: the ring shows nothing it was not told to.
 */
export function intentControlsOf(capabilities: RingCapabilities | null): IntentControls | null {
  if (!capabilities || capabilities.operatorIntentSignals !== true) return null;
  const c = capabilities.intentControls as Partial<Record<keyof IntentControls, unknown>> | undefined;
  return {
    showWatchers: c?.showWatchers === true,
    showBidderPresence: c?.showBidderPresence === true,
    showHoverIntent: c?.showHoverIntent === true,
    showConfirmIntent: c?.showConfirmIntent === true,
    showBidSubmitted: c?.showBidSubmitted === true,
    showBidderIdentity: c?.showBidderIdentity === true,
  };
}

/** ADR-70 cut 6f: a whole set of intent controls - the six booleans and
 *  nothing else. */
export function isIntentControls(v: unknown): v is IntentControls {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
  const o = v as Record<string, unknown>;
  return Object.keys(o).length === INTENT_CONTROL_KEYS.length && INTENT_CONTROL_KEYS.every((k) => typeof o[k] === 'boolean');
}

/** What the ring staff roster says about one floor staffer in one ring. */
export type RingStaffDecision =
  | { readonly ok: true }
  /** Bound to other rings of this sale only: these. */
  | { readonly ok: false; readonly rings: readonly string[] };

/**
 * ADR-70 cut 5f: may this floor staffer run this ring? Unbound (no roster,
 * or no entry, or an empty entry) is every ring, as before 5f. Pure; the
 * ring server asks it at HELLO and again when the roster changes.
 */
export function ringStaffAllows(roster: RingStaffRoster | null, staffId: string, ringId: string): RingStaffDecision {
  if (!roster || !Object.prototype.hasOwnProperty.call(roster.assignments, staffId)) return { ok: true };
  const rings = roster.assignments[staffId];
  if (!Array.isArray(rings) || rings.length === 0) return { ok: true };
  return rings.includes(ringId) ? { ok: true } : { ok: false, rings };
}

/**
 * ADR-70 cut 4 (hitlist section 37): the hitlist's permission names, each
 * mapped to the ring permissions that actually gate it. "Server must authorize
 * every command" already holds (the socket refuses before the engine, the
 * reducer refuses again); this table is so an auditor can read the hitlist's
 * word and find the check.
 *
 * `null` means no ring command exists yet for that act, so there is nothing to
 * authorize and nothing a client can send: the ring refuses an unknown command
 * as UNKNOWN_COMMAND before any role is consulted. Each one arrives with its
 * feature and its permission together (bidder intent, the emergency
 * stream). The increment is the sale's table, set in the office; since ADR-70
 * cut 6d the clerk may choose another for the lot in the ring (SET_INCREMENT).
 * Since cut 6e the clerk may look at the maxes on a lot (VIEW_MAX_BIDS) once
 * the Super Admin has switched the tenant's capability on; switching it is
 * not a ring act at all, so it stays null here.
 */
export type HitlistPermission =
  | 'operate_ring'
  | 'place_onsite_bid'
  | 'change_asking'
  | 'change_increment'
  | 'sell_lot'
  | 'pass_lot'
  | 'retract_bid'
  | 'reset_bidding'
  | 'reopen_lot'
  | 'override_online_onsite'
  | 'view_max_bid'
  | 'manage_max_bid_visibility'
  | 'manage_bidder_intent_visibility'
  | 'start_emergency_stream';

export const HITLIST_PERMISSIONS: { readonly [P in HitlistPermission]: readonly RingPermission[] | null } = {
  operate_ring: ['ring.set_current_lot', 'ring.open', 'ring.fair_warning', 'ring.hammer', 'ring.pause', 'ring.resume', 'ring.heartbeat', 'ring.post_announcement', 'ring.set_confirm_mode', 'ring.finalize', 'ring.remove_from_ring'],
  place_onsite_bid: ['ring.record_floor_bid'],
  change_asking: ['ring.set_ask'],
  change_increment: ['ring.change_increment'],
  sell_lot: ['ring.sell'],
  pass_lot: ['ring.pass', 'ring.no_sale'],
  retract_bid: ['ring.correct_floor_bid', 'ring.retract_bid'],
  reset_bidding: ['ring.reset_bidding'],
  reopen_lot: ['ring.reopen', 'ring.void_sale'],
  override_online_onsite: ['ring.accept_online_bid', 'ring.decline_online_bid', 'ring.award_equal_bid', 'ring.restore_equal_bid'],
  view_max_bid: ['ring.view_max_bids'],
  // ADR-70 cut 6e: the switch is the Super Admin's, on the platform console
  // (POST /platform/tenants/:slug/capabilities), never a ring command a
  // tenant's own staff could send; the ring only hears it from the control
  // plane (ring.set_ring_capabilities).
  manage_max_bid_visibility: null,
  manage_bidder_intent_visibility: null,
  start_emergency_stream: null,
};
